/**
 * Phone AI Assistant Types
 * Types for AWS Connect/Lex phone ordering, reservations, and waitlist
 */

// Phone call intent types
export type PhoneIntent =
  | 'PlaceOrder'
  | 'MakeReservation'
  | 'JoinWaitlist'
  | 'GetStoreInfo'
  | 'TransferToHuman'
  | 'Fallback';

// Call outcome types
export type CallOutcome =
  | 'completed'
  | 'transferred'
  | 'abandoned'
  | 'error';

// Phone features that can be enabled per store
export interface PhoneFeatures {
  ordering: boolean;
  reservations: boolean;
  waitlist: boolean;
  storeInfo: boolean;
}

// Store phone configuration
export interface PhoneConfig {
  storeId: string;
  enabled: boolean;
  phoneNumber?: string;
  connectInstanceId?: string;
  lexBotId?: string;
  lexBotAliasId?: string;
  features: PhoneFeatures;
  voiceId: string; // Amazon Polly voice
  greetingMessage: string;
  businessHours: BusinessHours;
  transferNumber: string;
  maxOrderTotal: number;
  requireCallback: boolean;
  callRecordingEnabled: boolean;
  transcriptEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

// Business hours configuration
export interface BusinessHours {
  monday: DayHours;
  tuesday: DayHours;
  wednesday: DayHours;
  thursday: DayHours;
  friday: DayHours;
  saturday: DayHours;
  sunday: DayHours;
}

// Hours for a single day
export interface DayHours {
  open: string; // "09:00"
  close: string; // "22:00"
  closed: boolean;
}

// Phone call record
export interface PhoneCall {
  callId: string;
  storeId: string;
  phoneNumber: string;
  intent: PhoneIntent;
  duration: number; // seconds
  outcome: CallOutcome;
  orderId?: string;
  reservationId?: string;
  waitlistEntryId?: string;
  transcript?: string;
  recordingUrl?: string;
  startTime: string;
  endTime: string;
  lexSessionId?: string;
}

// Phone order (created from phone call)
export interface PhoneOrder {
  orderId: string;
  callId: string;
  storeId: string;
  customerName: string;
  customerPhone: string;
  orderType: 'pickup' | 'delivery';
  items: PhoneOrderItem[];
  subtotal: number;
  tax: number;
  total: number;
  estimatedReadyTime: string;
  status: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'completed' | 'cancelled';
  deliveryAddress?: string;
  specialInstructions?: string;
  createdAt: string;
}

// Phone order item
export interface PhoneOrderItem {
  menuItemId: string;
  name: string;
  quantity: number;
  unitPrice: number;
  modifiers: string[];
  totalPrice: number;
}

// Phone reservation
export interface PhoneReservation {
  reservationId: string;
  callId: string;
  storeId: string;
  customerName: string;
  customerPhone: string;
  partySize: number;
  date: string;
  time: string;
  status: 'confirmed' | 'cancelled' | 'no-show' | 'seated' | 'completed';
  tableId?: string;
  specialRequests?: string;
  confirmationSent: boolean;
  reminderSent: boolean;
  createdAt: string;
}

// Phone waitlist entry
export interface PhoneWaitlistEntry {
  waitlistEntryId: string;
  callId: string;
  storeId: string;
  customerName: string;
  customerPhone: string;
  partySize: number;
  estimatedWait: number; // minutes
  status: 'waiting' | 'notified' | 'seated' | 'cancelled' | 'no-show';
  position: number;
  notifiedAt?: string;
  seatedAt?: string;
  createdAt: string;
}

// Amazon Polly voice options
export interface PollyVoice {
  voiceId: string;
  name: string;
  gender: 'Female' | 'Male';
  languageCode: string;
  neural: boolean;
}

// Available Polly voices for phone assistant
export const POLLY_VOICES: PollyVoice[] = [
  { voiceId: 'Joanna', name: 'Joanna', gender: 'Female', languageCode: 'en-US', neural: true },
  { voiceId: 'Matthew', name: 'Matthew', gender: 'Male', languageCode: 'en-US', neural: true },
  { voiceId: 'Ivy', name: 'Ivy', gender: 'Female', languageCode: 'en-US', neural: true },
  { voiceId: 'Kendra', name: 'Kendra', gender: 'Female', languageCode: 'en-US', neural: true },
  { voiceId: 'Kevin', name: 'Kevin', gender: 'Male', languageCode: 'en-US', neural: true },
  { voiceId: 'Salli', name: 'Salli', gender: 'Female', languageCode: 'en-US', neural: true },
  { voiceId: 'Joey', name: 'Joey', gender: 'Male', languageCode: 'en-US', neural: true },
];

// Lex session state
export interface LexSessionState {
  sessionId: string;
  intent: PhoneIntent;
  slots: Record<string, string | null>;
  confirmationState: 'None' | 'Confirmed' | 'Denied';
  dialogAction: 'ElicitSlot' | 'ConfirmIntent' | 'Close' | 'Delegate';
}

// Lex fulfillment request (from Lambda)
export interface LexFulfillmentRequest {
  sessionId: string;
  bot: {
    id: string;
    name: string;
    aliasId: string;
    localeId: string;
  };
  inputTranscript: string;
  interpretations: LexInterpretation[];
  sessionState: LexSessionState;
  requestAttributes?: Record<string, string>;
}

// Lex interpretation
export interface LexInterpretation {
  intent: {
    name: string;
    slots: Record<string, LexSlot | null>;
    state: 'InProgress' | 'ReadyForFulfillment' | 'Fulfilled' | 'Failed';
    confirmationState: 'None' | 'Confirmed' | 'Denied';
  };
  nluConfidence: {
    score: number;
  };
}

// Lex slot value
export interface LexSlot {
  value: {
    interpretedValue: string;
    originalValue: string;
    resolvedValues: string[];
  };
  shape: 'Scalar' | 'List';
}

// Lex fulfillment response
export interface LexFulfillmentResponse {
  sessionState: {
    dialogAction: {
      type: 'ElicitSlot' | 'ConfirmIntent' | 'Close' | 'Delegate';
      slotToElicit?: string;
    };
    intent: {
      name: string;
      state: 'InProgress' | 'ReadyForFulfillment' | 'Fulfilled' | 'Failed';
      slots: Record<string, LexSlot | null>;
    };
    sessionAttributes?: Record<string, string>;
  };
  messages?: LexMessage[];
}

// Lex message
export interface LexMessage {
  contentType: 'PlainText' | 'SSML' | 'CustomPayload';
  content: string;
}

// Phone analytics
export interface PhoneAnalytics {
  storeId: string;
  period: 'day' | 'week' | 'month';
  startDate: string;
  endDate: string;
  totalCalls: number;
  completedOrders: number;
  reservationsMade: number;
  waitlistAdditions: number;
  transfersToHuman: number;
  abandonedCalls: number;
  averageCallDuration: number;
  intentBreakdown: Record<PhoneIntent, number>;
  peakHours: { hour: number; count: number }[];
  totalRevenue: number;
  estimatedCost: number;
}

// Phone config update request
export interface UpdatePhoneConfigRequest {
  enabled?: boolean;
  features?: Partial<PhoneFeatures>;
  voiceId?: string;
  greetingMessage?: string;
  businessHours?: Partial<BusinessHours>;
  transferNumber?: string;
  maxOrderTotal?: number;
  requireCallback?: boolean;
  callRecordingEnabled?: boolean;
  transcriptEnabled?: boolean;
}

// Provision phone number request
export interface ProvisionPhoneNumberRequest {
  storeId: string;
  areaCode?: string;
  countryCode?: string;
}

// Phone number provision result
export interface ProvisionPhoneNumberResult {
  phoneNumber: string;
  connectInstanceId: string;
  lexBotId: string;
  lexBotAliasId: string;
  status: 'provisioned' | 'pending' | 'failed';
  error?: string;
}

// Store info response (for GetStoreInfo intent)
export interface StoreInfoResponse {
  storeName: string;
  address: string;
  phone: string;
  hours: string;
  currentlyOpen: boolean;
  todaysSpecials?: string[];
  waitTime?: number;
}

export default {
  POLLY_VOICES,
};
